<template>
  <v-container>
    <div
      v-if="loading"
      class="d-flex align-center justify-center"
      style="height: 100vh;"
    >
      <v-progress-circular indeterminate color="primary" size="80" width="2" />
    </div>
    <div v-else>
      <v-row class="px-2">
        <v-col cols="12" md="4">
          <h2
            class="text-h6 text_color font-weight-medium mb-2 ml-6 text-color"
            style="font-size: 20px !important;"
          >
            {{ $t('transactions.title') }}
          </h2>
          <v-breadcrumbs divider=">" :items="breadcrumbs" class="py-2" />
        </v-col>
        <v-col
          cols="12"
          md="8"
          class="d-flex justify-end align-center"
          style="gap: 4px;"
        >
          <DateBar
            :dateRange="dateRange"
            @updateRange="
              (value, range, index) => {
                dateRange = index
                transactionsTable.filters.endDate = $moment().format(
                  'YYYY-MM-DD'
                )
                changeDates(value, range)
              }
            "
            format-date="YYYY-MM-DD"
          />
          <DateFilters
            ref="date-filters"
            :initialDate="transactionsTable.filters.startDate"
            :finalDate="transactionsTable.filters.endDate"
            @updateInitialDate="updateDates('startDate', $event)"
            @updateFinalDate="updateDates('endDate', $event)"
          />
        </v-col>
      </v-row>
      <!------------------------------------------>
      <!-- TRANSACTIONS TABLE -------------------->
      <!------------------------------------------>
      <v-row no-gutters class="mt-6">
        <v-col cols="12">
          <v-card class="custom_card" flat>
            <v-card-title class="my-2">
              <h4 class="body-1 text_color font-weight-medium">
                <v-icon color="#344054" class="mb-1 mx-2">payments</v-icon>
                {{ $t('transactions.index.list') }}
              </h4>
            </v-card-title>
            <v-card-text>
              <v-row
                no-gutters
                class="mx-2 my-5 d-flex justify-center align-center"
                style="gap: 32px"
              >
                <!------------------------------------------>
                <!-- SEARCH -------------------------------->
                <!------------------------------------------>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="transactionsTable.filters.search"
                    outlined
                    clearable
                    dense
                    hide-details
                    class="rounded-lg text_color"
                    :label="$t('actions.search')"
                    prepend-inner-icon="search"
                    @keyup.enter="fetchTransactions"
                    @click:clear="clearSearch"
                  />
                </v-col>
                <!------------------------------------------>
                <!-- TYPE -------------------------------->
                <!------------------------------------------>
                <v-col cols="12" md="2">
                  <v-select
                    v-model="transactionsTable.filters.type"
                    :items="transactionTypes"
                    item-text="name"
                    item-value="id"
                    dense
                    outlined
                    hide-details
                    class="rounded-lg text_color"
                    :label="$t('transactions.index.table.headers.type')"
                    @change="fetchTransactions"
                  />
                </v-col>
                <!------------------------------------------>
                <!-- ORGANIZATION -------------------------->
                <!------------------------------------------>
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="transactionsTable.filters.organization_id"
                    :items="organizations"
                    item-text="name"
                    item-value="id"
                    dense
                    outlined
                    clearable
                    hide-details
                    class="rounded-lg text_color"
                    :label="$t('organization.organizations')"
                    @click:clear="clearSearch"
                    @change="fetchTransactions"
                  />
                </v-col>
                <!------------------------------------------>
                <!-- STATE -------------------------------->
                <!------------------------------------------>
                <v-col cols="12" md="2">
                  <v-select
                    v-model="transactionsTable.filters.state"
                    :items="transactionStates"
                    item-text="name"
                    item-value="id"
                    dense
                    outlined
                    clearable
                    hide-details
                    class="rounded-lg text_color"
                    :label="$t('transactions.index.table.headers.status')"
                    @click:clear="clearSearch"
                    @change="fetchTransactions"
                  />
                </v-col>
                <!------------------------------------------>
                <!-- GATEWAY -------------------------------->
                <!------------------------------------------>
                <v-col cols="12" md="2">
                  <v-select
                    v-model="transactionsTable.filters.gateway_id"
                    :items="gateways"
                    item-text="name"
                    item-value="id"
                    dense
                    outlined
                    clearable
                    hide-details
                    class="rounded-lg text_color"
                    :label="$t('transactions.index.table.headers.gateway')"
                    @click:clear="clearSearch"
                    @change="fetchTransactions"
                  />
                </v-col>
              </v-row>
              <v-data-table
                :loading="transactionsTable.loading"
                :items="transactionsTable.items"
                :server-items-length="transactionsTable.total"
                :items-per-page="transactionsTable.pagination.rowsPerPage"
                :headers="transactionsTable.headers"
                @update:options="transactionsTable.pagination = $event"
              >
                <template v-slot:no-data>
                  <div class="text-center pa-5 my-5">
                    <v-img
                      :src="require('@/assets/svg/box_empty.svg')"
                      height="150"
                      max-height="150px"
                      aspect-ratio="1"
                      contain
                    />
                    <div class="grey--text">
                      {{ $t('dashboard_clients.organizations.no_data') }}
                    </div>
                  </div>
                </template>

                <template v-slot:item.lead="{ item }">
                  <span>
                    {{ item.lead.first_name }} {{ item.lead.last_name }}
                  </span>
                </template>
                <template v-slot:item.organization="{ item }">
                  <span>
                    {{ item.organization.name }}
                  </span>
                </template>
                <template v-slot:item.amount="{ item }">
                  <span> $ {{ item.amount }} </span>
                </template>
                <template v-slot:item.state="{ item }">
                  <v-chip :color="getStateColor(item.state)">
                    {{ getStateByNumber(item.state) }}
                  </v-chip>
                </template>
                <template v-slot:item.created_at="{ item }">
                  <span>
                    {{ $moment(item.created_at).format('LLL') }}
                  </span>
                </template>
                <template v-slot:item.gateway="{ item }">
                  <span>
                    {{ item.directGateway.name }}
                  </span>
                </template>
                <template v-slot:item.type="{ item }">
                  <span>
                    {{ getFieldTransactionType(item) }}
                  </span>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import donationMixin from '@/mixins/donations'
import DateBar from '@/components/Common/DateBar/Index'
import { TransactionTypes } from '../../mixins/donations'
import DateFilters from '@/components/Common/DateFilters/Index'
import Vue from 'vue'

export default {
  name: 'Transactions',
  components: { DateBar, DateFilters },
  mixins: [donationMixin],
  data() {
    return {
      loading: false,
      breadcrumbs: [
        {
          text: this.$t('transactions.title'),
          disabled: true
        },
        {
          text: 'Index',
          disabled: false
        }
      ],
      dateRange: 3,
      transactionTypes: [],
      transactionStates: [],
      transactionsTable: {
        items: [],
        total: 0,
        loading: false,
        pagination: {
          page: 1,
          rowsPerPage: 10,
          descending: true,
          sortBy: ['created_at'],
          rowsPerPageItems: [10, 20, 30, 50, 100]
        },
        filters: {
          search: '',
          state: null,
          gateway_id: null,
          organization_id: null,
          type: TransactionTypes.ALL,
          startDate: this.$moment()
            .startOf('month')
            .format('YYYY-MM-DD'),
          endDate: this.$moment().format('YYYY-MM-DD')
        },
        headers: [
          {
            text: this.$t('transactions.index.table.headers.person'),
            align: 'left',
            sortable: false,
            value: 'lead',
            class: 'text-no-wrap text_color'
          },
          {
            text: this.$t('transactions.index.table.headers.organization'),
            align: 'left',
            sortable: false,
            value: 'organization',
            class: 'text-no-wrap text_color'
          },
          {
            text: this.$t('transactions.index.table.headers.amount'),
            align: 'left',
            sortable: true,
            value: 'amount',
            class: 'text-no-wrap text_color'
          },
          {
            text: this.$t('transactions.index.table.headers.status'),
            align: 'left',
            sortable: true,
            value: 'state',
            class: 'text-no-wrap text_color'
          },
          {
            text: this.$t('transactions.index.table.headers.date'),
            align: 'left',
            sortable: true,
            value: 'created_at',
            class: 'text-no-wrap text_color'
          },
          {
            text: this.$t('transactions.index.table.headers.gateway'),
            align: 'left',
            sortable: false,
            value: 'gateway',
            class: 'text-no-wrap text_color'
          },
          {
            text: this.$t('transactions.index.table.headers.type'),
            align: 'left',
            sortable: false,
            value: 'type',
            class: 'text-no-wrap text_color'
          }
        ]
      }
    }
  },
  async created() {
    const { fetchOrganizations, fetchGateways } = this

    this.transactionStates = this.getTransactionStates()
    this.transactionTypes = this.getTransactionTypes()

    await Promise.all([fetchOrganizations(), fetchGateways()])
  },
  watch: {
    'transactionsTable.pagination': {
      async handler() {
        await this.fetchTransactions()
      },
      deep: true
    }
  },
  computed: {
    ...mapState({
      total: state => state.transactions.total,
      gateways: state => state.gateways.allGateways,
      transactions: state => state.transactions.transactions,
      organizations: state => state.organization.organizations
    })
  },
  methods: {
    ...mapActions({
      fetchGateways: 'gateways/getAllGatewaysSelect',
      fetchOrganizations: 'organization/getOrganizations',
      fetchTransactionsAction: 'transactions/fetchTransactions'
    }),
    getFieldTransactionType(item) {
      const { is_subscription, monthly_plan_charge } = item

      if (monthly_plan_charge && !is_subscription) {
        return this.$t('transactions.types.charge')
      } else if (is_subscription && !monthly_plan_charge) {
        return this.$t('transactions.types.subscription')
      } else {
        return this.$t('transactions.types.unique')
      }
    },
    updateDates(property, date) {
      const { transactionsTable } = this
      const { filters } = transactionsTable

      filters[property] = date

      const startDate = this.$moment(filters.startDate).unix()
      const endDate = this.$moment(filters.endDate)
        .endOf('day')
        .unix()

      this.changeDates('customized', startDate, endDate)
    },
    async changeDates(code, start, end) {
      const { transactionsTable } = this
      const { filters } = transactionsTable

      const startDate = start || this.$moment(filters.startDate).startOf('day')
      const endDate = end || this.$moment(filters.endDate).endOf('day')

      filters.startDate = this.$moment(startDate).format('YYYY-MM-DD')
      filters.endDate = this.$moment(endDate).format('YYYY-MM-DD')

      if (code === 'customized') this.dateRange = 7

      await this.fetchTransactions()
    },
    async fetchTransactions() {
      const { filters, pagination } = this.transactionsTable
      let { sortBy } = pagination

      if (Array.isArray(sortBy)) {
        ;[sortBy] = sortBy
      }

      try {
        this.transactionsTable.loading = true

        await this.fetchTransactionsAction({
          ...filters,
          ...pagination,
          sortBy: sortBy || 'created_at'
        })

        this.transactionsTable.items = this.transactions
        this.transactionsTable.total = parseInt(this.total, 10)

        this.transactionsTable.loading = false
      } catch (error) {
        Vue.noty.error(
          `${this.$t('transactions.messages.error.fetch')}: ${error}`
        )
      }
    },
    async clearSearch() {
      this.transactionsTable.filters.search = ''
      await this.fetchTransactions()
    }
  }
}
</script>

<style lang="scss">
.custom_card {
  border-radius: 12px !important;
  border: 1px solid #d0d3db !important;
}

.text_color {
  color: #344054 !important;
}
</style>
